import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/brand/Logo'

/**
 * Layout per le pagine pre-auth. Split-screen su desktop:
 *   - sinistra: foto editoriale pulita (hidden < md)
 *   - destra: logo + form su sfondo piatto
 *
 * Su mobile (< md) collassa al solo lato destro.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="grid h-full min-h-screen grid-cols-1 md:grid-cols-2">
      <HeroPanel />

      <section className="relative flex flex-col overflow-hidden bg-background">
        <GlowDecor />

        <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
          <div className="flex w-full max-w-sm flex-col items-center gap-8">
            <Logo size="lg" variant="full" />
            {children}
          </div>
        </div>

        <PrivacyFooter />
      </section>
    </div>
  )
}

function HeroPanel() {
  return (
    <aside aria-hidden className="relative hidden overflow-hidden bg-muted md:block">
      <img
        src="/energy-coach.webp"
        srcSet="/energy-coach@1x.webp 800w, /energy-coach.webp 1200w, /energy-coach@2x.webp 1536w"
        sizes="50vw"
        alt=""
        loading="eager"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
    </aside>
  )
}

/**
 * Glow decor distribuito (non centrato) per dare profondità al pannello
 * destro del login. Due "blob" radiali cyan in posizioni asimmetriche,
 * morbidi e poco saturi. Più visibili in dark mode.
 */
function GlowDecor() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {/* blob principale in alto-destra: cyan saturo */}
      <div
        className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full opacity-[0.45] blur-3xl dark:opacity-[0.4]"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--secondary) / 0.95), hsl(var(--secondary) / 0))',
        }}
      />
      {/* blob in basso-sinistra: navy con sfumatura cyan */}
      <div
        className="absolute -bottom-40 -left-32 h-[460px] w-[460px] rounded-full opacity-[0.35] blur-3xl dark:opacity-[0.34]"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--primary) / 0.85), hsl(var(--primary) / 0))',
        }}
      />
      {/* scintilla cyan piccola al centro-destra */}
      <div
        className="absolute right-[20%] top-[30%] h-40 w-40 rounded-full opacity-[0.35] blur-2xl dark:opacity-[0.4]"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--secondary) / 1), hsl(var(--secondary) / 0))',
        }}
      />
    </div>
  )
}

function PrivacyFooter() {
  return (
    <p className="relative z-10 px-6 pb-4 text-center text-[11px] leading-snug text-muted-foreground">
      Autenticandoti acconsenti al trattamento dei tuoi dati e dichiari di aver letto l’{' '}
      <Link
        to="/privacy"
        className="font-semibold text-secondary underline-offset-2 hover:underline"
      >
        Informativa privacy
      </Link>
      .
    </p>
  )
}
